import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const ErrorTestStyle = styled.div`
  padding: 20px;
  background: #0B0F1B;
  color: #ffffff;
  min-height: 100vh;

  .test-section {
    max-width: 600px;
    margin: 0 auto;
    padding: 20px;
    background: rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .test-title {
    font-size: 24px;
    font-weight: 600;
    margin-bottom: 20px;
    color: #ffffff;
  }

  .test-description {
    font-size: 16px;
    color: #b8b8b8;
    margin-bottom: 24px;
    line-height: 1.6;
  }

  .test-buttons {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .test-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 12px 20px;
    height: 48px;
    background: #007aff;
    font-family: Poppins;
    font-size: 14px;
    font-weight: 600;
    line-height: 1;
    letter-spacing: -0.01em;
    text-align: center;
    border-radius: 24px;
    border: none;
    color: #fff;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 2px 12px rgba(0, 122, 255, 0.3);

    &:hover {
      background: #0056cc;
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(0, 122, 255, 0.4);
    }

    &.danger {
      background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
      box-shadow: 0 2px 12px rgba(255, 107, 107, 0.3);

      &:hover {
        background: linear-gradient(135deg, #ff5252 0%, #e53935 100%);
        box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4);
      }
    }

    &.warning {
      background: linear-gradient(135deg, #ffb74d 0%, #f57c00 100%);
      box-shadow: 0 2px 12px rgba(255, 183, 77, 0.3);

      &:hover {
        background: linear-gradient(135deg, #ff9800 0%, #ef6c00 100%);
        box-shadow: 0 6px 20px rgba(255, 183, 77, 0.4);
      }
    }
  }

  .error-details {
    margin-top: 20px;
    padding: 16px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 12px;
    color: #ff6b6b;
    white-space: pre-wrap;
  }
`;

const ErrorThrowingComponent: React.FC = () => {
  useEffect(() => {
    // This component always throws an error when rendered
    throw new Error('This is a test error from ErrorThrowingComponent');
  }, []);

  return <div>This should never render</div>;
};

const ErrorTest: React.FC = () => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showThrowingComponent, setShowThrowingComponent] = useState(false);

  const throwSyncError = () => {
    throw new Error('Synchronous error thrown manually');
  };

  const throwAsyncError = async () => {
    // Simulate async operation that fails
    try {
      await new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Async operation failed')), 1000);
      });
    } catch (error) {
      throw error;
    }
  };

  const throwNestedError = () => {
    const nestedFunction = () => {
      const evenMoreNested = () => {
        throw new Error('Deeply nested error for testing stack traces');
      };
      evenMoreNested();
    };
    nestedFunction();
  };

  const causeRenderError = () => {
    // This will cause a render error
    setErrorMessage(null as any); // TypeScript will catch this, but runtime will error
  };

  const causeUndefinedError = () => {
    const obj: any = null;
    console.log(obj.nonexistent.property); // Will throw TypeError
  };

  if (showThrowingComponent) {
    return <ErrorThrowingComponent />;
  }

  return (
    <ErrorTestStyle>
      <div className="test-section">
        <h1 className="test-title">🧪 Error Boundary Test Page</h1>

        <p className="test-description">
          This page contains various buttons to test the Error Boundary. Each button will trigger
          a different type of error. Click any button to see the Error Boundary in action!
        </p>

        <div className="test-buttons">
          <button className="test-button danger" onClick={throwSyncError}>
            🚨 Throw Synchronous Error
          </button>

          <button className="test-button danger" onClick={throwAsyncError}>
            ⚡ Throw Async Error
          </button>

          <button className="test-button danger" onClick={throwNestedError}>
            🔍 Throw Nested Error (Stack Trace)
          </button>

          <button className="test-button warning" onClick={causeRenderError}>
            🎨 Cause Render Error
          </button>

          <button className="test-button warning" onClick={causeUndefinedError}>
            ❓ Cause Undefined Property Error
          </button>

          <button
            className="test-button"
            onClick={() => setShowThrowingComponent(true)}
          >
            🔄 Show Component That Always Throws
          </button>
        </div>

        {errorMessage && (
          <div className="error-details">
            <strong>Last Error:</strong>
            {errorMessage}
          </div>
        )}

        <div style={{ marginTop: '30px', padding: '20px', background: 'rgba(0, 122, 255, 0.1)', borderRadius: '8px' }}>
          <h3 style={{ color: '#007aff', marginBottom: '10px' }}>💡 Tips for Testing:</h3>
          <ul style={{ color: '#b8b8b8', lineHeight: '1.6' }}>
            <li>Try each button to see different error types</li>
            <li>Check browser console for error details</li>
            <li>Use "Try Again" button in error boundary to retry</li>
            <li>In production, error details are hidden for security</li>
          </ul>
        </div>
      </div>
    </ErrorTestStyle>
  );
};

export default ErrorTest;
